import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trophy, Star, Medal, Crown, Search, Filter, X,
  ArrowUpRight, ChevronUp, ChevronDown, Download,
  Award, Shield, Clock, DollarSign, ThumbsUp, MapPin,
  Users, BarChart2, Calendar, ArrowUp, ArrowDown,
  ChevronLeft, ChevronRight
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import { getAdminRankings } from "../../redux/Actions/AdminProfessionalScoringActions";
import { RootState, AppDispatch } from "../../redux/store";

const Rankingsadmin = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { 
    rankings, 
    rankingsLoading, 
    rankingsError 
  } = useSelector((state: RootState) => state.adminProfessionalScoring);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedTimeframe, setSelectedTimeframe] = useState("month");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Debounce timer ref
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Debounced search effect
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery]);

  // Fetch data when page, debounced search, or timeframe changes
  useEffect(() => {
    dispatch(getAdminRankings({
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearchQuery || undefined,
      timeframe: selectedTimeframe,
    }));
  }, [dispatch, currentPage, itemsPerPage, debouncedSearchQuery, selectedTimeframe]);

  // Use real data from Redux store
  const rankingsData = rankings?.rankings || [];
  const totalCount = rankings?.totalCount || 0;
  const totalPages = rankings?.totalPages || 1;
  const hasNextPage = rankings?.hasNextPage || false;

  // Calculate pagination display
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalCount);

  // Reset to page 1 when search or timeframe changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, selectedTimeframe]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery("");
  };

  // Handle export rankings
  const handleExportRankings = () => {
    // TODO: Implement export functionality
    alert("Export functionality coming soon! This will download rankings as CSV/Excel.");
  };

  // Loading state
  if (rankingsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading rankings...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (rankingsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{rankingsError}</p>
          <button 
            onClick={() => dispatch(getAdminRankings({
              page: currentPage,
              limit: itemsPerPage,
              search: debouncedSearchQuery || undefined,
              timeframe: selectedTimeframe,
            }))} 
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Get timeframe label
  const getTimeframeLabel = () => {
    switch (selectedTimeframe) {
      case "quarter":
        return "This Quarter";
      case "year":
        return "This Year";
      default:
        return "This Month";
    }
  };

  const RankingCard = ({ professional }) => (
    <Card className="p-4 sm:p-6 hover:shadow-md transition-shadow"   style={{ textAlign: "left" }}>
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className={`h-10 sm:h-12 w-10 sm:w-12 rounded-full flex items-center justify-center ${
            professional.rank.overall === 1 ? "bg-yellow-100" :
            professional.rank.overall === 2 ? "bg-gray-100" :
            "bg-orange-100"
          }`}>
            {professional.rank.overall === 1 ? (
              <Trophy className="h-5 sm:h-6 w-5 sm:w-6 text-yellow-600" />
            ) : professional.rank.overall === 2 ? (
              <Medal className="h-5 sm:h-6 w-5 sm:w-6 text-gray-600" />
            ) : (
              <Medal className="h-5 sm:h-6 w-5 sm:w-6 text-orange-600" />
            )}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold text-base sm:text-lg">{professional.name}</h3>
              <Badge variant={undefined} className="bg-blue-100 text-blue-700 text-xs sm:text-sm">
                #{professional.rank.overall}
              </Badge>
              {professional.rank.trend === "up" ? (
                <Badge variant={undefined} className="bg-green-100 text-green-700">
                  <ArrowUp className="h-3 w-3" />
                </Badge>
              ) : (
                <Badge variant={undefined} className="bg-red-100 text-red-700">
                  <ArrowDown className="h-3 w-3" />
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-600 mt-1">
              <span>{professional.profession}</span>
              <span>•</span>
              <div className="flex items-center gap-1">
                <MapPin className="h-3 sm:h-4 w-3 sm:w-4" />
                {professional.region}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 sm:mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        <div>
          <div className="text-xs sm:text-sm text-gray-500">Rating</div>
          <div className="flex items-center gap-2 mt-1">
            <div className="text-base sm:text-xl font-semibold">{professional.metrics.rating}</div>
            <Star className="h-4 sm:h-5 w-4 sm:w-5 text-yellow-400 fill-current" />
          </div>
        </div>
        <div>
          <div className="text-xs sm:text-sm text-gray-500">Total Jobs</div>
          <div className="flex items-center gap-2 mt-1">
            <div className="text-base sm:text-xl font-semibold">{professional.metrics.totalJobs}</div>
            <Badge variant={undefined} className="bg-green-100 text-green-700 text-xs">
              {professional.performance.trend.jobs}
            </Badge>
          </div>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <div className="text-xs sm:text-sm text-gray-500">Revenue</div>
          <div className="flex items-center gap-2 mt-1">
            <div className="text-base sm:text-xl font-semibold">{professional.metrics.revenue}</div>
            <Badge variant={undefined} className="bg-green-100 text-green-700 text-xs">
              {professional.performance.trend.revenue}
            </Badge>
          </div>
        </div>
      </div>

      {professional.achievements && professional.achievements.length > 0 && (
        <div className="mt-4 sm:mt-6">
          <div className="text-xs sm:text-sm text-gray-500 mb-2">Achievements</div>
          <div className="flex flex-wrap gap-2">
            {professional.achievements.map((achievement, index) => (
              <Badge  variant={undefined}
                key={index}
                className={`flex items-center gap-1 text-xs ${
                  achievement.type === "crown" ? "bg-yellow-100 text-yellow-700" :
                  achievement.type === "star" ? "bg-blue-100 text-blue-700" :
                  "bg-green-100 text-green-700"
                }`}
              >
                {achievement.type === "crown" ? <Crown className="h-3 w-3" /> :
                 achievement.type === "star" ? <Star className="h-3 w-3" /> :
                 <Shield className="h-3 w-3" />}
                {achievement.label}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t">
        <div className="text-xs sm:text-sm text-gray-500 mb-2">{getTimeframeLabel()}&apos;s Performance</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="p-2 sm:p-3 bg-gray-50 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-500">Jobs</div>
            <div className="mt-1 text-sm sm:text-base font-semibold">
              {professional.performance.thisMonth.jobs}
            </div>
          </div>
          <div className="p-2 sm:p-3 bg-gray-50 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-500">Revenue</div>
            <div className="mt-1 text-sm sm:text-base font-semibold">
              {professional.performance.thisMonth.revenue}
            </div>
          </div>
          <div className="col-span-2 sm:col-span-1 p-2 sm:p-3 bg-gray-50 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-500">Rating</div>
            <div className="mt-1 text-sm sm:text-base font-semibold">
              {professional.performance.thisMonth.rating}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col"   style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-3 sm:p-6">
          <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Rankings</h1>
                <p className="text-sm sm:text-base text-gray-600 mt-1">View and analyze professional rankings</p>
              </div>
              <Button 
                variant="outline"
                className="flex items-center gap-2 text-sm w-full sm:w-auto"
                onClick={handleExportRankings}
              >
                <Download className="h-4 w-4" />
                Export Rankings
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <Card className="p-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Top Performers</p>
                    <p className="text-lg sm:text-2xl font-semibold mt-1">
                      {rankings?.topPerformers || 0}
                    </p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Trophy className="h-4 sm:h-5 w-4 sm:w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Rising Stars</p>
                    <p className="text-lg sm:text-2xl font-semibold mt-1">
                      {rankings?.risingStars || 0}
                    </p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ArrowUpRight className="h-4 sm:h-5 w-4 sm:w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Categories</p>
                    <p className="text-lg sm:text-2xl font-semibold mt-1">
                      {rankings?.categories || 0}
                    </p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <BarChart2 className="h-4 sm:h-5 w-4 sm:w-5 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Regions</p>
                    <p className="text-lg sm:text-2xl font-semibold mt-1">
                      {rankings?.regions || 0}
                    </p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <MapPin className="h-4 sm:h-5 w-4 sm:w-5 text-green-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Timeframe */}
            <Card className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 sm:h-5 w-4 sm:w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search professionals by name or profession..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-full pl-10 pr-10 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {searchQuery && (
                    <button
                      onClick={handleClearSearch}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Clear search"
                    >
                      <X className="h-4 sm:h-5 w-4 sm:w-5" />
                    </button>
                  )}
                </div>

                {debouncedSearchQuery && debouncedSearchQuery !== searchQuery && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span>Searching...</span>
                  </div>
                )}
                
                <select
                  value={selectedTimeframe}
                  onChange={(e) => setSelectedTimeframe(e.target.value)}
                  className="px-4 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="month">This Month</option>
                  <option value="quarter">This Quarter</option>
                  <option value="year">This Year</option>
                </select>
              </div>
            </Card>

            {/* Rankings Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {rankingsData.length > 0 ? (
                rankingsData.map(professional => (
                  <RankingCard key={professional.id} professional={professional} />
                ))
              ) : (
                <div className="col-span-2 text-center py-12">
                  <p className="text-gray-500">No rankings found matching your criteria</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {startIndex + 1} to {endIndex} of {totalCount} professionals
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="flex items-center gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        // Show first page, last page, current page, and pages around current page
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`px-3 py-1 rounded ${
                                currentPage === page
                                  ? "bg-blue-600 text-white"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (
                          page === currentPage - 2 ||
                          page === currentPage + 2
                        ) {
                          return <span key={page} className="px-2">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="flex items-center gap-1"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Rankingsadmin;